import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaSave, FaTimes, FaSpinner, FaTrash } from "react-icons/fa";

const ZonesForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    name: "",
    warehouseId: "",
  });

  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<any[]>([]);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  const fetchData = async () => {
    try {
      const resWarehouses = await axios.get(`${API}/warehouses`, getAuthHeader());
      const sortedWarehouses = resWarehouses.data.data.sort((a: any, b: any) =>
        a.name.localeCompare(b.name)
      );
      setWarehouses(sortedWarehouses);

      if (isEdit) {
        const resZone = await axios.get(`${API}/zones/${id}`, getAuthHeader());
        setFormData({
          name: resZone.data.name || "",
          warehouseId: resZone.data.warehouseId || "",
        });
      }
    } catch (error) {
      console.error("Fetch data error", error);
      setWarehouses([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    if (!formData.name || !formData.warehouseId) {
      alert("Please fill all mandatory fields.");
      return;
    }

    const confirmSave = window.confirm("Do you want to save this data?");
    if (!confirmSave) return;

    setLoading(true);

    try {
      const payload = {
        ...formData,
        tenantId: user.tenantId,
        createdByUserId: user.id,
      };

      if (isEdit) {
        await axios.put(`${API}/zones/${id}`, payload, getAuthHeader());
        alert("Zone updated!");
      } else {
        await axios.post(`${API}/zones`, payload, getAuthHeader());
        alert("Zone created!");
      }

      navigate("/zone");
    } catch (error) {
      console.error("Failed to save zone:", error);
      alert("Failed to save zone");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this zone?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/zones/${id}`, getAuthHeader());
      alert("Zone deleted");
      navigate("/zone");
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete zone");
    }
  };

  return (
    <div className="p-1 max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-700">
            {isEdit ? "Edit Zone" : "Add Zone"}
          </h2>
          <div className="flex gap-2">
            <button
              type="submit"
              form="zone-form"
              className="bg-blue-600 text-white px-2 py-1 rounded text-[13px] flex items-center gap-1 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
              Save
            </button>
            <button
              onClick={() => navigate("/zone")}
              className="bg-gray-300 text-gray-700 px-2 py-1 rounded text-[13px] flex items-center gap-1"
            >
              <FaTimes />
              Cancel
            </button>
            {isEdit && (
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-2 py-1 rounded text-[13px] flex items-center gap-1"
                disabled={loading}
              >
                <FaTrash />
                Delete
              </button>
            )}
          </div>
        </div>

        <form id="zone-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[13px] font-medium">
                Zone Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="border rounded px-2 py-1 text-[13px]"
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[13px] font-medium">
                Warehouse <span className="text-red-500">*</span>
              </label>
              <select
                name="warehouseId"
                value={formData.warehouseId}
                onChange={handleChange}
                className="border rounded px-2 py-1 text-[13px]"
                required
              >
                <option value="">-- Select Warehouse --</option>
                {warehouses.map((wh) => (
                  <option key={wh.id} value={wh.id}>
                    {wh.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ZonesForm;
